import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faGraduationCap, faChalkboardTeacher, faChess, faChild, faCalendarAlt, faBook, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { applications } from '../../data/applications';
import '../Applications.css';

// Import images
import applicationsHeroImage from '../../assets/images/duyurular.jpg';

const ApplicationsEN = () => {
  const popularApps = applications.filter(app => app.isPopular);
  const newApps = applications.filter(app => app.isNew);
  const internationalApps = applications.filter(app => app.category === 'Yurtdışı Eğitimi');
  const otherApps = applications.filter(app => !app.isPopular && !app.isNew && app.category !== 'Yurtdışı Eğitimi');

  const getIcon = (iconName) => {
    const icons = {
      academy: faGraduationCap,
      tutor: faChalkboardTeacher,
      ib: faBook,
      chess: faChess,
      kids: faChild,
      program: faCalendarAlt,
      edtech: faBook,
      publisher: faBuilding
    };
    return icons[iconName] || faGraduationCap;
  };

  // Translate application data
  const translateApp = (app) => {
    const translations = {
      'progenx-academy': {
        title: 'ProgenX Academy',
        description: 'Education technology application',
        category: 'Education Technologies'
      },
      'ozel-ders': {
        title: 'Private Tutoring Platform',
        description: 'Platform connecting students seeking private lessons with teachers',
        category: 'Education Platform'
      },
      'ib-egitimi': {
        title: 'IB Education Platform',
        description: 'Comprehensive platform related to IB education',
        category: 'Education Platform'
      },
      'chess': {
        title: 'ProgenX Chess',
        description: 'Chess game',
        category: 'Game'
      },
      'kids': {
        title: 'ProgenX Kids',
        description: 'Educational application for children',
        category: 'Children Education'
      },
      'program': {
        title: 'Class Schedule Platform',
        description: 'Platform for schools to create their own schedules',
        category: 'School Management'
      },
      'edtech': {
        title: 'EdTech Content Platform',
        description: 'Platform for teachers to develop content',
        category: 'Content Development'
      },
      'yayinevi-kitap': {
        title: 'Sample Book Application for Publishers',
        description: 'Publishers upload books from admin panel, select specific parts, create links and send digital versions to teachers for book reviews',
        category: 'Publisher Solutions'
      },
      'imat': {
        title: 'IMAT Platform',
        description: 'IMAT education and assessment platform',
        category: 'International Education'
      },
      'alevel': {
        title: 'A-Level Platform',
        description: 'Comprehensive education platform for A-Level exam preparation',
        category: 'International Education'
      },
      'sat': {
        title: 'SAT Platform',
        description: 'Comprehensive education platform for SAT exam preparation',
        category: 'International Education'
      },
      'ap': {
        title: 'AP Platform',
        description: 'Comprehensive education platform for Advanced Placement (AP) exam preparation',
        category: 'International Education'
      },
      'act': {
        title: 'ACT Platform',
        description: 'Comprehensive education platform for ACT exam preparation',
        category: 'International Education'
      }
    };
    
    const translation = translations[app.id];
    if (translation) {
      return {
        ...app,
        title: translation.title,
        description: translation.description,
        category: translation.category
      };
    }
    return app;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "ProgenX Applications",
    "description": "ProgenX's education technology, private tutoring, IB education, chess, and children education applications",
    "url": "https://progenxacademy.com/en/applications",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": applications.map((app, index) => {
        const translated = translateApp(app);
        return {
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "SoftwareApplication",
            "name": translated.title,
            "description": translated.description,
            "url": app.url,
            "applicationCategory": translated.category,
            "operatingSystem": "Web"
          }
        };
      })
    }
  };

  return (
    <div className="applications">
      <Helmet>
        <title>ProgenX Applications | Education Technologies and Platforms 2025</title>
        <meta name="description" content="ProgenX's education technology applications: ProgenX Academy, Private Tutoring Platform, IB Education, ProgenX Chess, ProgenX Kids, Class Schedule, and EdTech Content Platform. AI-powered learning solutions." />
        <meta name="keywords" content="progenx applications, progenx academy, private tutoring platform, ib education, progenx chess, progenx kids, class schedule, edtech platform, education technology, ai education" />
        <meta property="og:title" content="ProgenX Applications | Education Technology Platforms" />
        <meta property="og:description" content="Discover ProgenX's education technologies, private tutoring, IB education, and children education applications." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/en/applications" />
        <link rel="canonical" href="https://progenxacademy.com/en/applications" />
        <link rel="alternate" hreflang="tr" href="https://progenxacademy.com/uygulamalar" />
        <link rel="alternate" hreflang="en" href="https://progenxacademy.com/en/applications" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <Hero 
        title="OUR APPLICATIONS" 
        backgroundImage={applicationsHeroImage}
      />
      
      <section className="applications-content">
        <div className="container">
          <h2 className="section-title">Our Applications</h2>
          <p className="applications-intro">
            As ProgenX, we develop various platforms and applications in the field of education technologies. 
            Each one offers solutions specifically designed for different needs.
          </p>

          {popularApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Popular Applications</h3>
              <div className="applications-grid">
                {popularApps.map(app => {
                  const translated = translateApp(app);
                  return (
                    <div 
                      key={app.id}
                      className="application-card"
                    >
                      <a 
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-external-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Go to App
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                      <div className="app-icon">
                        <FontAwesomeIcon icon={getIcon(app.icon)} />
                      </div>
                      <h4 className="app-title">{translated.title}</h4>
                      <p className="app-description">{translated.description}</p>
                      <Link 
                        to={app.id === 'progenx-academy' ? '/en/academy' : `/en/applications/${app.slug}`}
                        className="app-btn-inspect"
                      >
                        Explore
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {newApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">New Applications</h3>
              <div className="applications-grid">
                {newApps.map(app => {
                  const translated = translateApp(app);
                  return (
                    <div 
                      key={app.id}
                      className="application-card"
                    >
                      <a 
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-external-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Go to App
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                      <div className="app-icon">
                        <FontAwesomeIcon icon={getIcon(app.icon)} />
                      </div>
                      <h4 className="app-title">{translated.title}</h4>
                      <p className="app-description">{translated.description}</p>
                      <Link 
                        to={`/en/applications/${app.slug}`}
                        className="app-btn-inspect"
                      >
                        Explore
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {otherApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Other Applications</h3>
              <div className="applications-grid">
                {otherApps.map(app => {
                  const translated = translateApp(app);
                  return (
                    <div 
                      key={app.id}
                      className="application-card"
                    >
                      <a 
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-external-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Go to App
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                      <div className="app-icon">
                        <FontAwesomeIcon icon={getIcon(app.icon)} />
                      </div>
                      <h4 className="app-title">{translated.title}</h4>
                      <p className="app-description">{translated.description}</p>
                      <Link 
                        to={`/en/applications/${app.slug}`}
                        className="app-btn-inspect"
                      >
                        Explore
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {internationalApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">International Education</h3>
              <div className="applications-grid">
                {internationalApps.map(app => {
                  const translated = translateApp(app);
                  return (
                    <div 
                      key={app.id}
                      className="application-card"
                    >
                      <a 
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-external-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Go to App
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                      <div className="app-icon">
                        <FontAwesomeIcon icon={getIcon(app.icon)} />
                      </div>
                      <h4 className="app-title">{translated.title}</h4>
                      <p className="app-description">{translated.description}</p>
                      <Link 
                        to={`/en/applications/${app.slug}`}
                        className="app-btn-inspect"
                      >
                        Explore
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
};

export default ApplicationsEN;
